import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, message, Row, Col, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { updateContact } from '../../../MessagesServices';
import { useErrorHandler } from '../../../../../Hooks/useErrorHandler';

const { Option } = Select;
const { useBreakpoint } = Grid;

const ContactEditModal = ({ visible, onCancel, contact, onSuccess }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const { handleError } = useErrorHandler();
    const screens = useBreakpoint();

    useEffect(() => {
        if (contact) {
            form.setFieldsValue({
                contact_type: contact.contact_type || 'customer',
                first_name: contact.first_name || '',
                last_name: contact.last_name || '',
                profile_url: contact.profile_url || '',
                locale: contact.locale || '',
                timezone: contact.timezone || 0,
                gender: contact.gender || '',
                email: contact.email || '',
                phone: contact.phone || '',
                note: contact.note || ''
            });
        }
    }, [contact, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            await updateContact(contact.id, values);
            message.success(t('contact_updated_successfully'));
            onCancel();
        } catch (error) {
            console.log('error', error);
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const phonePattern = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/;

    const modalWidth = screens.xs ? '100%' : 800;
    
    const mobileStyles = screens.xs ? {
        maxWidth: '100vw',
        margin: 0,
        top: 0,
        padding: 0,
        height: '100vh',
    } : {};

    const bodyStyle = screens.xs ? {
        padding: '12px 8px',
        maxHeight: 'calc(100vh - 55px)',
        overflowY: 'auto',
        overflowX: 'hidden',
    } : {};

    const rowStyle = {
        marginLeft: 0,
        marginRight: 0,
    };
    return (
        <Modal
            title={t('edit_contact_information')}
            open={visible}
            onCancel={onCancel}
            footer={null}
            destroyOnClose
            width={modalWidth}
            style={mobileStyles}
            bodyStyle={bodyStyle}
            centered={!screens.xs}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    contact_type: contact?.contact_type || 'customer',
                    first_name: contact?.first_name || '',
                    last_name: contact?.last_name || '',
                    profile_url: contact?.profile_url || '',
                    locale: contact?.locale || '',
                    timezone: contact?.timezone || 0,
                    gender: contact?.gender || '',
                    email: contact?.email || '',
                    phone: contact?.phone || '',
                    note: contact?.note || ''
                }}
                style={{ width: '100%', overflowX: 'hidden' }}
            >
                <Row gutter={[{ xs: 4, sm: 8, md: 16 }, 8]} style={rowStyle}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            disabled
                            name="contact_type"
                            label={t('contact_type')}
                            rules={[{ required: true, message: t('please_select_contact_type') }]}
                        >
                            <Select disabled>
                                <Option value="customer">{t('customer')}</Option>
                                <Option value="admin">{t('admin')}</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="gender"
                            label={t('gender')}
                        >
                            <Select>
                                <Option value="male">{t('male')}</Option>
                                <Option value="female">{t('female')}</Option>
                                <Option value="other">{t('other')}</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[{ xs: 4, sm: 8, md: 16 }, 8]} style={rowStyle}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="first_name"
                            label={t('first_name')}
                            rules={[{ required: true, message: t('please_enter_first_name') }]}
                        >
                            <Input placeholder={t('enter_first_name')} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="last_name"
                            label={t('last_name')}
                            rules={[{ required: true, message: t('please_enter_last_name') }]}
                        >
                            <Input placeholder={t('enter_last_name')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[{ xs: 4, sm: 8, md: 16 }, 8]} style={rowStyle}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="email"
                            label={t('email')}
                            rules={[
                                { type: 'email', message: t('please_enter_valid_email') },
                                { 
                                    validator: (_, value) => {
                                        if (!value || (value && value.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/))) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(t('please_enter_valid_email')));
                                    }
                                }
                            ]}
                        >
                            <Input placeholder={t('enter_email')} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="phone"
                            label={t('phone')}
                            rules={[
                                {
                                    pattern: phonePattern,
                                    message: t('please_enter_valid_phone'),
                                    validator: (_, value) => {
                                        if (!value || phonePattern.test(value)) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(t('please_enter_valid_phone')));
                                    }
                                }
                            ]}
                        >
                            <Input placeholder={t('enter_phone')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[{ xs: 4, sm: 8, md: 16 }, 8]} style={rowStyle}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="locale"
                            label={t('locale')}
                            rules={[
                                {
                                    pattern: /^[a-z]{2}(_[A-Z]{2})?$/,
                                    message: t('please_enter_valid_locale'),
                                }
                            ]}
                        >
                            <Input placeholder={t('enter_locale')} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="timezone"
                            label={t('timezone')}
                            rules={[
                                {
                                    type: 'number',
                                    min: -12,
                                    max: 14,
                                    message: t('please_enter_valid_timezone'),
                                    transform: (value) => {
                                        if (value === '') return null;
                                        return Number(value);
                                    }
                                }
                            ]}
                        >
                            <Input type="number" min={-12} max={14} placeholder={t('enter_timezone')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[{ xs: 4, sm: 8, md: 16 }, 8]} style={rowStyle}>
                    <Col xs={24}>
                        <Form.Item
                            name="profile_url"
                            label={t('profile_url')}
                            rules={[
                                {
                                    type: 'url',
                                    message: t('please_enter_valid_url'),
                                    validator: (_, value) => {
                                        if (!value || /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/.test(value)) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(t('please_enter_valid_url')));
                                    }
                                }
                            ]}
                        >
                            <Input placeholder={t('enter_profile_url')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[{ xs: 4, sm: 8, md: 16 }, 8]} style={rowStyle}>
                    <Col xs={24}>
                        <Form.Item
                            name="note"
                            label={t('note')}
                        >
                            <Input.TextArea rows={4} placeholder={t('enter_note')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item>
                    <Button 
                        type="primary" 
                        block 
                        onClick={handleSubmit} 
                        loading={loading}
                    >
                        {t('update_contact')}
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ContactEditModal; 